import React, { useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon, FullscreenOutlined as FullscreenIcon } from '@mui/icons-material';
import SafeMarkdown from '../../shared/SafeMarkdown';
import { assistantChatMarkdownSx } from '../../shared/markdownBodySx';
import { colors } from '../../../designTokens';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../../freestyle/assistantButtonStyles';
import GeneratedCapabilityFullscreenDialog from './GeneratedCapabilityFullscreenDialog';
import { assistantDocumentSx } from './assistantDocumentStyles';
import KnowledgeReviewCard from './KnowledgeReviewCard';

export default function GeneratedCapabilityCard({
  promptText = '',
  onApply,
  onExpand,
  onPreview,
  applying = false,
  appliedToShelf = false,
  title = 'Capability Created',
  applyLabel = 'Apply to Workspace',
  nextStepTitle,
  nextStepDescription,
  onFindKnowledge,
  knowledgeStatus = 'idle',
  knowledgeReview = null,
  onAttachKnowledge,
  onAddKnowledgeResult,
  onOpenKnowledgeResult,
  onTest,
  testLabel = 'Test capability',
  onRun,
  onRefine,
  previewable = false,
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const canExpand = Boolean(onExpand || previewable);
  const isCheckingKnowledge = knowledgeStatus === 'checking';
  const hasReviewedKnowledge = knowledgeStatus === 'ready';
  const showNextStep = Boolean(nextStepTitle || nextStepDescription || (!knowledgeReview && !onTest));
  const openPreview = () => {
    if (onExpand) onExpand(promptText);
    else setPreviewOpen(true);
  };
  return (
    <Stack spacing={1} sx={{ mt: 1.25 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 1.75,
          borderRadius: '8px',
          position: 'relative',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={0.85} alignItems="center" sx={{ mb: 0.75, pr: canExpand ? 4 : 0 }}>
          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9375rem', letterSpacing: 0 }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
        {canExpand && (
          <IconButton
            size="small"
            aria-label="Expand capability"
            title="Expand capability"
            onClick={openPreview}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              zIndex: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.1)',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)', color: 'text.primary' },
            }}
          >
            <FullscreenIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            maxHeight: 'min(52vh, 520px)',
            minHeight: { xs: 160, sm: 200 },
            overflow: 'auto',
            pr: 1,
            '& p': { m: 0, mb: 0.8 },
          }}
        >
          <SafeMarkdown markdownBaseSx={assistantChatMarkdownSx} sx={assistantDocumentSx}>{promptText}</SafeMarkdown>
        </Box>
      </Paper>
      {knowledgeReview && (
        <Box
          sx={{
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <KnowledgeReviewCard
            {...knowledgeReview}
            compact
            showActions={false}
            onAddResult={onAddKnowledgeResult}
            onOpenResult={onOpenKnowledgeResult}
          />
        </Box>
      )}
      {showNextStep && (
        <Box
          sx={{
            py: 1,
          }}
        >
          <Typography sx={{ color: colors.textPrimary, fontSize: '0.8125rem', fontWeight: 750 }}>
            {nextStepTitle || (appliedToShelf ? 'Added to the builder' : 'Test and tune this draft')}
          </Typography>
          <Typography sx={{ mt: 0.25, color: colors.textSecondary, fontSize: '0.8125rem', lineHeight: 1.45 }}>
            {nextStepDescription || (appliedToShelf
              ? 'Generate details when you are ready, or use the builder to save and publish this capability.'
              : 'Add supporting knowledge, test an example output, or type refinements below before adding it to the builder.')}
          </Typography>
        </Box>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {onTest && (
          <Button
            variant="outlined"
            size="small"
            disabled={applying}
            onClick={() => onTest(promptText)}
            sx={assistantSecondaryButtonSx}
          >
            {testLabel}
          </Button>
        )}
        {onFindKnowledge && !hasReviewedKnowledge && (
          <Button
            variant="outlined"
            size="small"
            disabled={applying || isCheckingKnowledge}
            onClick={() => onFindKnowledge(promptText)}
            sx={assistantTextButtonSx}
          >
            {hasReviewedKnowledge ? 'Refresh knowledge' : 'Find knowledge'}
          </Button>
        )}
        {onAttachKnowledge && (
          <Button
            variant="outlined"
            size="small"
            disabled={applying || isCheckingKnowledge}
            onClick={onAttachKnowledge}
            sx={assistantSecondaryButtonSx}
          >
            Add knowledge
          </Button>
        )}
        {onRun && (
          <Button
            variant="outlined"
            size="small"
            disabled={applying}
            onClick={() => onRun(promptText)}
            sx={assistantSecondaryButtonSx}
          >
            Run capability
          </Button>
        )}
        {onRefine && (
          <Button
            variant="outlined"
            size="small"
            disabled={applying}
            onClick={() => onRefine(promptText)}
            sx={assistantSecondaryButtonSx}
          >
            Refine capability
          </Button>
        )}
        {onApply && (
          <Button
            variant="contained"
            size="small"
            disabled={applying}
            onClick={() => onApply(promptText)}
            sx={assistantPrimaryButtonSx}
          >
            {applyLabel}
          </Button>
        )}
        {onPreview && (
          <Button
            variant="text"
            size="small"
            onClick={() => (appliedToShelf ? onPreview(promptText) : openPreview())}
            sx={assistantTextButtonSx}
          >
            Preview
          </Button>
        )}
      </Stack>
      <GeneratedCapabilityFullscreenDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        promptText={promptText}
        title={title}
      />
    </Stack>
  );
}
